import React, { useState, useEffect } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { FileCode, FileText, Image } from 'lucide-react';
import { cn } from '../lib/utils';

interface LadderDropdownMenuProps {
  onExportCSV: () => void;
  onExportJSON: () => void;
  onExportSVG: () => void;
  onExportPNG: () => void;
  children: React.ReactNode;
  disabled?: boolean; // Add disabled prop for empty canvas
}

const LadderDropdownMenu: React.FC<LadderDropdownMenuProps> = ({
  onExportCSV,
  onExportJSON,
  onExportSVG,
  onExportPNG,
  children,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Reset visible items when opening
      setVisibleItems([]);
      
      // Animate items appearing one by one like ladder rungs
      const items = [0, 1, 2, 3]; // CSV, JSON, SVG, PNG
      items.forEach((itemIndex, delay) => {
        setTimeout(() => {
          setVisibleItems(prev => [...prev, itemIndex]);
        }, delay * 100); // 100ms delay between each item
      });
    } else {
      // Hide items in reverse order when closing
      const items = [3, 2, 1, 0]; // PNG, SVG, JSON, CSV
      items.forEach((itemIndex, delay) => {
        setTimeout(() => {
          setVisibleItems(prev => prev.filter(item => item !== itemIndex));
        }, delay * 50); // 50ms delay for closing
      });
    }
  }, [isOpen]);

  const menuItems = [
    {
      icon: FileText,
      label: 'CSV (Relationships)',
      onClick: disabled ? undefined : onExportCSV,
      index: 0,
    },
    {
      icon: FileCode,
      label: 'JSON (Data)',
      onClick: disabled ? undefined : onExportJSON,
      index: 1,
    },
    {
      icon: Image,
      label: 'SVG (Vector)',
      onClick: disabled ? undefined : onExportSVG,
      index: 2,
    },
    {
      icon: Image,
      label: 'PNG (Image)',
      onClick: disabled ? undefined : onExportPNG,
      index: 3,
    },
  ];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="min-w-[12rem] overflow-hidden rounded-xl border border-slate-200/60 bg-white/95 backdrop-blur-[20px] p-2 text-slate-700 shadow-[0_8px_25px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] z-[9999]"
        side="bottom"
        sideOffset={2}
      >
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isVisible = visibleItems.includes(item.index);
          
          return (
            <DropdownMenuItem
              key={item.index}
              onClick={item.onClick}
              disabled={disabled}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-300 ease-out",
                disabled 
                  ? "cursor-not-allowed opacity-50" 
                  : "cursor-pointer hover:bg-slate-100/80 hover:shadow-sm focus:bg-slate-100/80 focus:outline-none",
                isVisible 
                  ? "opacity-100 transform translate-y-0 scale-100" 
                  : "opacity-0 transform translate-y-2 scale-95"
              )}
              style={{
                transitionDelay: isOpen ? `${item.index * 100}ms` : `${(menuItems.length - 1 - item.index) * 50}ms`,
              }}
            >
              <Icon className="h-4 w-4 text-slate-600 group-hover:text-slate-800 transition-colors" />
              <span className="font-medium">{item.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LadderDropdownMenu;
